import { stripMarkdown } from 'lib/string/stripMarkdown'

// Mirrors svc-trip assistant-surface protocol string limits.
export const ASSISTANT_SURFACE_LABEL_MAX_LENGTH = 80
export const ASSISTANT_SURFACE_SOURCE_PATH_MAX_LENGTH = 240
export const ASSISTANT_SURFACE_ATOM_TEXT_PLATFORM_MAX_LENGTH = 600
export const ASSISTANT_SURFACE_ATOM_TEXT_OFFER_PAGE_MAX_LENGTH = 400
export const ASSISTANT_SURFACE_TARGET_TEXT_MAX_LENGTH = 200
export const ASSISTANT_SURFACE_MANIFEST_FACT_MAX_LENGTH = 512

const MARKDOWN_LINK = /!?\[([^\]]+)\]\([^)]*\)/g
const HTML_TAG = /<[^>]*>/g
const URL_SCHEME = /(?:^|[\s([])[A-Za-z][A-Za-z0-9+.-]*:\/\/[^\s)\]]+/g
const DISALLOWED_TEXT_PROTOCOL = /(?:^|[\s([])(?:surface-target|hotel|experience|cruise|place|place-search|http|https|ftp|javascript|data|vbscript|file|mailto):[^\s)\]]+/gi

function replaceControlCharacters(value: string): string {
  return [...value].map(character => {
    const codePoint = character.codePointAt(0)
    return codePoint !== undefined && (codePoint < 0x20 || codePoint === 0x7F) ? ' ' : character
  }).join('')
}

function stripUrlsAndProtocols(value: string): string {
  return value
    .replace(URL_SCHEME, ' ')
    .replace(DISALLOWED_TEXT_PROTOCOL, ' ')
}

export function sanitizeOfferPageSnapshotPlainText(value: string | undefined | null): string | undefined {
  if (!value) {
    return undefined
  }

  const withoutMarkup = replaceControlCharacters(
    stripUrlsAndProtocols(
      stripMarkdown(
        value
          .replace(MARKDOWN_LINK, '$1')
          .replace(HTML_TAG, ' '),
      ),
    ),
  )
    .replace(/\s+/g, ' ')
    .trim()

  return withoutMarkup || undefined
}

function boundedPlainText(value: string | undefined | null, maxLength: number): string | undefined {
  if (!value) {
    return undefined
  }

  const sanitized = sanitizeOfferPageSnapshotPlainText(value)
  if (!sanitized) {
    return undefined
  }

  const truncated = sanitized.length > maxLength ? sanitized.slice(0, maxLength) : sanitized
  return truncated || undefined
}

function findAtomTextSplitIndex(text: string, maxLength: number): number {
  if (text.length <= maxLength) {
    return text.length
  }

  const preferredBreaks = [
    text.lastIndexOf('\n\n', maxLength),
    text.lastIndexOf('. ', maxLength),
    text.lastIndexOf(' ', maxLength),
  ]

  for (const breakIndex of preferredBreaks) {
    if (breakIndex > maxLength * 0.5) {
      return breakIndex
    }
  }

  return maxLength
}

export function splitOfferPageSnapshotAtomText(
  value: string | undefined | null,
  maxSegmentLength: number = ASSISTANT_SURFACE_ATOM_TEXT_OFFER_PAGE_MAX_LENGTH,
): Array<string> {
  const sanitized = value ? sanitizeOfferPageSnapshotPlainText(value) : undefined
  if (!sanitized) {
    return []
  }

  const segmentLimit = Math.min(maxSegmentLength, ASSISTANT_SURFACE_ATOM_TEXT_PLATFORM_MAX_LENGTH)
  if (sanitized.length <= segmentLimit) {
    return [sanitized]
  }

  const segments: Array<string> = []
  let remaining = sanitized

  while (remaining.length > segmentLimit) {
    const splitIndex = findAtomTextSplitIndex(remaining, segmentLimit)
    const segment = remaining.slice(0, splitIndex).trim()
    if (segment) {
      segments.push(segment)
    }
    remaining = remaining.slice(splitIndex).trim()
  }

  if (remaining) {
    segments.push(remaining)
  }

  return segments
}

export function toOfferPageSnapshotLabel(value: string | undefined | null): string | undefined {
  return boundedPlainText(value, ASSISTANT_SURFACE_LABEL_MAX_LENGTH)
}

export function toOfferPageSnapshotSourcePath(value: string | undefined | null): string | undefined {
  return boundedPlainText(value, ASSISTANT_SURFACE_SOURCE_PATH_MAX_LENGTH)
}

export function toOfferPageSnapshotAtomText(value: string | undefined | null): string | undefined {
  return sanitizeOfferPageSnapshotPlainText(value ?? undefined)
}

export function toOfferPageSnapshotTargetText(value: string | undefined | null): string | undefined {
  return boundedPlainText(value, ASSISTANT_SURFACE_TARGET_TEXT_MAX_LENGTH)
}

export function toOfferPageSnapshotManifestFactText(value: string | undefined | null): string | undefined {
  return boundedPlainText(value, ASSISTANT_SURFACE_MANIFEST_FACT_MAX_LENGTH)
}

// Flattens structured facility groups (name + list of item names) into plain text for an atom's
// displayText -- shared across flash/property structuredFacilities and bedbank facilityGroups.
export function formatFacilityGroupsText(groups: Array<{ name: string, itemNames: Array<string> }>): string | undefined {
  const lines = groups
    .filter(group => group.itemNames.length > 0)
    .map(group => `${group.name}: ${group.itemNames.join(', ')}`)

  return lines.length ? lines.join('\n\n') : undefined
}

function formatAmenityGroupsText(
  amenityGroups: Array<{ name: string, items: Array<{ name: string }> }>,
): string | undefined {
  return formatFacilityGroupsText(
    amenityGroups.map(group => ({
      name: group.name,
      itemNames: group.items.map(item => item.name),
    })),
  )
}

// Mirrors PropertyNavigableOfferContent facilities fallback: structuredFacilities, then
// legacy facilities markdown, then amenity groups.
export function getPropertyFacilitiesSnapshotText(
  property: Pick<App.Property.Property, 'structuredFacilities' | 'facilities' | 'amenities'>,
): string | undefined {
  if (property.structuredFacilities.length) {
    return formatFacilityGroupsText(
      property.structuredFacilities.map(group => ({
        name: group.group.name,
        itemNames: group.facilities.map(facility => facility.name),
      })),
    )
  }

  if (property.facilities) {
    return property.facilities
  }

  if (property.amenities.length) {
    return formatAmenityGroupsText(property.amenities)
  }

  return undefined
}

export function toOfferPageSnapshotId(value: string | undefined): string | undefined {
  const normalized = value
    ?.trim()
    .replace(/[^A-Za-z0-9._:-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')

  return normalized || undefined
}
